import React, { useState, useRef, useCallback, useMemo } from "react";
import { useAuth } from "../../../context/AuthContext";
import { FaChevronDown, FaChevronUp } from "react-icons/fa";
import { sanitizeErrorMessage } from "../../../utility/errorSanitization";

// Mobile Card Component - Separate from table structure
export const BowlerScoreMobileCard = ({
  bowler,
  teamId,
  gamesPerEvent,
  onScoreUpdate,
  calculatingTotals,
  getCalculationResult,
  canEditScores = false,
  teamTransformedScores = {},
  isScoreSaving = () => false,
}) => {
  const [validationErrors, setValidationErrors] = useState({});
  const [isMobileExpanded, setIsMobileExpanded] = useState(false);
  const [localScores, setLocalScores] = useState({});

  // Get bowler totals from cache
  const bowlerScratchTotal = useMemo(() => {
    const result =
      getCalculationResult &&
      getCalculationResult(teamId, bowler.id, "scratch");
    return result?.total || 0;
  }, [getCalculationResult, teamId, bowler.id]);

  const bowlerHandicapTotal = useMemo(() => {
    const result =
      getCalculationResult &&
      getCalculationResult(teamId, bowler.id, "handicap");
    return result?.handicapTotal || 0;
  }, [getCalculationResult, teamId, bowler.id]);

  // Get bowler scores
  const bowlerScores = useMemo(() => {
    if (!teamTransformedScores || !teamTransformedScores[bowler.id]) {
      return [];
    }
    return Array.from(
      { length: gamesPerEvent },
      (_, i) => teamTransformedScores[bowler.id][i] || { score: null }
    );
  }, [teamTransformedScores, bowler.id, gamesPerEvent]);

  // Validate score input
  const validateScore = useCallback((value) => {
    if (value === "") return null;
    const score = parseInt(value);
    if (isNaN(score) || score < 0 || score > 300) {
      return "Score must be between 0 and 300";
    }
    return null;
  }, []);

  // Handle score input change
  const handleScoreChange = useCallback(
    (gameIndex, value) => {
      // Don't allow score changes if editing is disabled
      if (!canEditScores) {
        return;
      }

      if (validationErrors[gameIndex]) {
        setValidationErrors((prev) => ({ ...prev, [gameIndex]: null }));
      }

      // Update local state for immediate UI update
      setLocalScores((prev) => ({
        ...prev,
        [gameIndex]: value === "" ? "" : value,
      }));
    },
    [validationErrors, canEditScores]
  );

  // Handle score blur (save)
  const handleScoreBlur = useCallback(
    async (gameIndex, value) => {
      // Don't allow score updates if editing is disabled
      if (!canEditScores) {
        return;
      }

      const validationError = validateScore(value);
      if (validationError) {
        setValidationErrors((prev) => ({
          ...prev,
          [gameIndex]: validationError,
        }));
        return;
      }

      const newScore = value === "" ? null : parseInt(value);
      const currentScore = bowlerScores[gameIndex]?.score;

      if (newScore !== currentScore && onScoreUpdate) {
        try {
          setValidationErrors((prev) => ({ ...prev, [gameIndex]: null }));
          await onScoreUpdate(teamId, bowler.id, gameIndex, newScore);
        } catch (error) {
          console.error("Error saving score:", error);
          setValidationErrors((prev) => ({
            ...prev,
            [gameIndex]: sanitizeErrorMessage(error, "save score"),
          }));
        }
      }
    },
    [
      bowlerScores,
      onScoreUpdate,
      teamId,
      bowler.id,
      validateScore,
      canEditScores,
    ]
  );

  const isCalculatingTotals =
    calculatingTotals && typeof calculatingTotals.has === "function"
      ? calculatingTotals.has(teamId)
      : false;

  return (
    <div className="md:hidden card-inset mb-3 overflow-hidden w-full">
      {/* Header - Always Visible */}
      <div
        className="p-4 cursor-pointer hover:bg-brand-cream/80 transition-colors"
        onClick={() => setIsMobileExpanded(!isMobileExpanded)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsMobileExpanded(!isMobileExpanded);
          }
          if (e.key === "Escape" && isMobileExpanded) {
            setIsMobileExpanded(false);
          }
        }}
        tabIndex={0}
        role="button"
        aria-expanded={isMobileExpanded}
        aria-controls={`expanded-content-${bowler.id}`}
        aria-label={`${isMobileExpanded ? "Collapse" : "Expand"} ${
          bowler.name
        } score details`}
      >
        <div className="flex justify-between items-center">
          <div className="flex-1">
            <h3 className="font-display font-bold text-brand-charcoal text-lg">
              {bowler.name}
            </h3>
            <div className="flex items-center gap-4 mt-1 text-sm text-brand-muted">
              <div className="flex items-center gap-2">
                <span>
                  Avg:{" "}
                  {bowler.hasAverage === false ? "N/A" : bowler.average || 0}
                </span>
                {bowler.isVerified && (
                  <span
                    className="border border-brand-charcoal/20 text-brand-charcoal px-1.5 py-0.5 rounded-lg cursor-help text-xs"
                    title="Verified"
                  >
                    ✓
                  </span>
                )}
              </div>
              {bowler.handicap !== null && bowler.handicap !== undefined && (
                <span>Hdcp: {bowler.handicap}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {isCalculatingTotals ? (
              <span className="text-brand-mist text-sm">Calculating...</span>
            ) : (
              <div className="text-right">
                <div className="flex items-center gap-3">
                  <div>
                    <div className="text-xs text-brand-muted">Scratch</div>
                    <div className="font-score">{bowlerScratchTotal}</div>
                  </div>
                  <div>
                    <div className="text-xs text-brand-muted">Hdcp</div>
                    <div className="font-score">{bowlerHandicapTotal}</div>
                  </div>
                </div>
              </div>
            )}
            {isMobileExpanded ? (
              <FaChevronUp className="w-5 h-5 text-brand-muted" />
            ) : (
              <FaChevronDown className="w-5 h-5 text-brand-muted" />
            )}
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      {isMobileExpanded && (
        <div className="border-t border-brand-charcoal/10 p-4 bg-brand-cream/50">
          {/* Side Pots */}
          {bowler.eventEntries && bowler.eventEntries.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-brand-charcoal mb-2">
                Side Pots
              </h4>
              <ul className="flex flex-wrap gap-2">
                {bowler.eventEntries
                  .filter((entry) => !entry.isPrimary)
                  .map((entry, index) => (
                    <li
                      key={
                        entry.id ||
                        entry.eventsID ||
                        `event-${index}-${entry.name}`
                      }
                    >
                      <span
                        className="inline-block px-2 py-1 text-xs border border-brand-charcoal/15 text-brand-muted rounded-lg"
                        title={entry.name}
                      >
                        {entry.abbreviation || entry.name}
                      </span>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {/* Game Scores */}
          <div className="mb-4">
            <h4 className="text-sm font-medium text-brand-charcoal mb-3">
              Game Scores
            </h4>
            <div className="grid grid-cols-3 gap-3">
              {Array.from({ length: Math.max(1, gamesPerEvent) }).map(
                (_, gameIndex) => (
                  <div key={gameIndex} className="flex flex-col">
                    <label className="text-xs text-brand-muted mb-1 text-center">
                      Game {gameIndex + 1}
                    </label>
                    <input
                      type="number"
                      data-game-index={gameIndex}
                      className={`border text-center w-full h-12 rounded-lg font-score focus:outline-none focus:ring-2 focus:ring-brand-gold-ink ${
                        canEditScores ? "bg-white" : "bg-brand-cream"
                      } ${
                        validationErrors[gameIndex]
                          ? "border-red-300"
                          : "border-brand-charcoal/20"
                      }`}
                      min="0"
                      max="300"
                      value={
                        localScores[gameIndex] !== undefined
                          ? localScores[gameIndex]
                          : bowlerScores[gameIndex]?.score || ""
                      }
                      onBlur={
                        canEditScores
                          ? (e) => handleScoreBlur(gameIndex, e.target.value)
                          : undefined
                      }
                      onChange={
                        canEditScores
                          ? (e) => handleScoreChange(gameIndex, e.target.value)
                          : undefined
                      }
                      disabled={!canEditScores}
                      placeholder="0-300"
                      title={canEditScores ? "Enter score (0-300)" : undefined}
                      aria-label={`${bowler.name} - Game ${
                        gameIndex + 1
                      } score`}
                      aria-invalid={!!validationErrors[gameIndex]}
                    />
                    {validationErrors[gameIndex] && (
                      <p
                        className="text-xs text-red-500 mt-1 text-center"
                        role="alert"
                      >
                        {validationErrors[gameIndex]}
                      </p>
                    )}
                    {isScoreSaving(teamId, bowler.id, gameIndex) && (
                      <p className="text-xs text-brand-muted mt-1 text-center">
                        Saving...
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          </div>

          {/* Totals */}
          <div className="grid grid-cols-2 gap-4">
            <div className="text-center">
              <div className="text-sm text-brand-muted">Scratch Total</div>
              <div className="font-score text-lg">
                {isCalculatingTotals ? (
                  <span className="text-brand-mist">Calculating...</span>
                ) : (
                  bowlerScratchTotal
                )}
              </div>
            </div>
            <div className="text-center">
              <div className="text-sm text-brand-muted">Handicap Total</div>
              <div className="font-score text-lg">
                {isCalculatingTotals ? (
                  <span className="text-brand-mist">Calculating...</span>
                ) : (
                  bowlerHandicapTotal
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BowlerScoreRow = React.memo(
  ({
    bowler,
    teamId,
    gamesPerEvent,
    isEven,
    onScoreUpdate = () => {},
    calculatingTotals = new Set(),
    getCalculationResult = () => null,
    canEditScores = false,
    teamTransformedScores = {},
    isScoreSaving = () => false,
  }) => {
    const [validationErrors, setValidationErrors] = useState({});
    const [isMobileExpanded, setIsMobileExpanded] = useState(false);
    const [localScores, setLocalScores] = useState({});

    // NEW: Get bowler totals from cache instead of bowler state
    const bowlerScratchTotal = useMemo(() => {
      const result =
        getCalculationResult &&
        getCalculationResult(teamId, bowler.id, "scratch");
      const total = result?.total || 0;
      return total;
    }, [getCalculationResult, teamId, bowler.id, bowler.name]);

    const bowlerHandicapTotal = useMemo(() => {
      const result =
        getCalculationResult &&
        getCalculationResult(teamId, bowler.id, "handicap");
      const total = result?.handicapTotal || 0;
      return total;
    }, [getCalculationResult, teamId, bowler.id, bowler.name]);

    // Memoize the bowler scores to prevent unnecessary re-renders
    const bowlerScores = useMemo(() => {
      if (!teamTransformedScores || !teamTransformedScores[bowler.id]) {
        return [];
      }

      const scores = Array.from(
        { length: gamesPerEvent },
        (_, i) => teamTransformedScores[bowler.id][i] || { score: null }
      );

      return scores;
    }, [teamTransformedScores, bowler.id, bowler.name, gamesPerEvent]);

    // Validate score input
    const validateScore = useCallback((value) => {
      if (value === "" || value === null) return null;

      const numValue = parseInt(value);
      if (isNaN(numValue)) return "Score must be a number";
      if (numValue < 0) return "Score cannot be negative";
      if (numValue > 300) return "Score cannot exceed 300";

      return null;
    }, []);

    // Handle score input blur - save on blur
    const handleScoreBlur = useCallback(
      async (gameIndex, value) => {
        // Don't allow score updates if editing is disabled
        if (!canEditScores) {
          return;
        }

        const validationError = validateScore(value);
        if (validationError) {
          setValidationErrors((prev) => ({
            ...prev,
            [gameIndex]: validationError,
          }));
          return;
        }

        const newScore = value === "" ? null : parseInt(value);
        const currentScore = bowlerScores[gameIndex]?.score;

        // Only save if the score actually changed and onScoreUpdate is available
        if (newScore !== currentScore && onScoreUpdate) {
          try {
            setValidationErrors((prev) => ({ ...prev, [gameIndex]: null }));
            await onScoreUpdate(teamId, bowler.id, gameIndex, newScore);
          } catch (error) {
            console.error("Error saving score:", error);
            setValidationErrors((prev) => ({
              ...prev,
              [gameIndex]: sanitizeErrorMessage(error, "save score"),
            }));
          }
        }
      },
      [
        bowlerScores,
        onScoreUpdate,
        teamId,
        bowler.id,
        validateScore,
        canEditScores,
      ]
    );

    // Handle score input key down - Excel-like navigation
    const handleScoreKeyDown = useCallback(
      async (e, gameIndex) => {
        // Don't allow score updates if editing is disabled
        if (!canEditScores) {
          return;
        }

        if (e.key === "Enter") {
          e.preventDefault();

          const value = e.target.value;
          const validationError = validateScore(value);
          if (validationError) {
            setValidationErrors((prev) => ({
              ...prev,
              [gameIndex]: validationError,
            }));
            return;
          }

          // Save current score
          const newScore = value === "" ? null : parseInt(value);
          const currentScore = bowlerScores[gameIndex]?.score;

          if (newScore !== currentScore) {
            try {
              setValidationErrors((prev) => ({ ...prev, [gameIndex]: null }));

              if (onScoreUpdate) {
                await onScoreUpdate(teamId, bowler.id, gameIndex, newScore);
              }
            } catch (error) {
              console.error("Error saving score:", error);
              setValidationErrors((prev) => ({
                ...prev,
                [gameIndex]: sanitizeErrorMessage(error, "save score"),
              }));
              return; // Don't navigate if save failed
            }
          }

          // Move to next input (Excel-like behavior)
          const nextGameIndex = gameIndex + 1;
          if (nextGameIndex < gamesPerEvent) {
            const nextInput =
              e.target.parentElement.parentElement.parentElement.querySelector(
                `input[data-game-index="${nextGameIndex}"]`
              );
            if (nextInput) {
              nextInput.focus();
              nextInput.select();
            }
          }
        } else if (e.key === "Tab") {
          // Save on Tab before moving
          const value = e.target.value;
          const validationError = validateScore(value);
          if (validationError) {
            e.preventDefault();
            setValidationErrors((prev) => ({
              ...prev,
              [gameIndex]: validationError,
            }));
            return;
          }

          const newScore = value === "" ? null : parseInt(value);
          const currentScore = bowlerScores[gameIndex]?.score;

          if (newScore !== currentScore && onScoreUpdate) {
            try {
              setValidationErrors((prev) => ({ ...prev, [gameIndex]: null }));

              await onScoreUpdate(teamId, bowler.id, gameIndex, newScore);
            } catch (error) {
              console.error("Error saving score:", error);
              setValidationErrors((prev) => ({
                ...prev,
                [gameIndex]: sanitizeErrorMessage(error, "save score"),
              }));
              e.preventDefault(); // Prevent tab navigation if save failed
            }
          }
        }
      },
      [bowlerScores, onScoreUpdate, teamId, bowler.id, validateScore]
    );

    // Handle score input change
    const handleScoreChange = useCallback(
      (gameIndex, value) => {
        // Don't allow score changes if editing is disabled
        if (!canEditScores) {
          return;
        }

        // Clear validation error when user starts typing
        if (validationErrors[gameIndex]) {
          setValidationErrors((prev) => ({ ...prev, [gameIndex]: null }));
        }

        // Update local state for immediate UI update
        setLocalScores((prev) => ({
          ...prev,
          [gameIndex]: value === "" ? "" : value,
        }));
      },
      [validationErrors, canEditScores]
    );

    // Check if this team is currently calculating totals
    const isCalculatingTotals =
      calculatingTotals && typeof calculatingTotals.has === "function"
        ? calculatingTotals.has(teamId)
        : false;

    return (
      <tr
        className={`mb-0.5 ${
          isEven ? "bg-brand-cream/50" : "bg-white"
        } hidden md:table-row`}
      >
        {/* Side Pots - hidden on mobile */}
        <td className="items-center hidden md:table-cell p-2.5 text-center">
          {bowler.eventEntries && bowler.eventEntries.length > 0 ? (
            <span className="flex flex-wrap gap-1 justify-center">
              {bowler.eventEntries
                .filter((entry) => !entry.isPrimary)
                .map((entry, index) => (
                  <span
                    key={
                      entry.id ||
                      entry.eventsID ||
                      `event-${index}-${entry.name}`
                    }
                    className="inline-block px-2 py-1 text-xs border border-brand-charcoal/15 text-brand-muted rounded-lg"
                    title={entry.name}
                  >
                    {entry.abbreviation || entry.name}
                  </span>
                ))}
            </span>
          ) : (
            <span className="text-brand-mist">-</span>
          )}
        </td>

        {/* Bowler Name */}
        <td className="font-semibold md:table-cell p-2.5 text-center">
          {bowler.name}
        </td>

        {/* Bowler Average - hidden on mobile */}
        <td className="hidden md:table-cell p-2.5 text-center font-score">
          <span className="flex items-center gap-2 justify-center">
            {bowler.hasAverage === false ? "N/A" : bowler.average || 0}
            {bowler.isVerified && (
              <span
                className="border border-brand-charcoal/20 text-brand-charcoal px-1.5 py-0.5 rounded-lg cursor-help text-xs"
                title="Verified"
              >
                ✓
              </span>
            )}
          </span>
        </td>

        {/* Bowler Handicap - hidden on mobile */}
        <td className="hidden md:table-cell p-2.5 text-center font-score">
          {bowler.handicap !== null && bowler.handicap !== undefined ? (
            bowler.handicap
          ) : (
            <span className="text-brand-mist">-</span>
          )}
        </td>

        {/* Game Scores */}
        <td colSpan={Math.max(1, gamesPerEvent)} className="text-center p-2.5">
          {/* Desktop view only */}
          <div className="hidden md:flex justify-between">
            {Array.from({ length: Math.max(1, gamesPerEvent) }).map(
              (_, gameIndex) => (
                <div
                  key={gameIndex}
                  className="flex flex-col items-center mx-1"
                >
                  <div className="flex items-center gap-1 mb-1">
                    <input
                      type="number"
                      data-game-index={gameIndex}
                      className={`border text-center rounded-lg font-score focus:outline-none focus:ring-2 focus:ring-brand-gold-ink ${
                        canEditScores ? "w-16" : "w-20"
                      } ${
                        canEditScores
                          ? "bg-white hover:bg-brand-cream/50"
                          : "bg-brand-cream"
                      }`}
                      min="0"
                      max="300"
                      value={
                        localScores[gameIndex] !== undefined
                          ? localScores[gameIndex]
                          : bowlerScores[gameIndex]?.score || ""
                      }
                      onBlur={
                        canEditScores
                          ? (e) => handleScoreBlur(gameIndex, e.target.value)
                          : undefined
                      }
                      onKeyDown={
                        canEditScores
                          ? (e) => handleScoreKeyDown(e, gameIndex)
                          : undefined
                      }
                      onChange={
                        canEditScores
                          ? (e) => handleScoreChange(gameIndex, e.target.value)
                          : undefined
                      }
                      title={
                        canEditScores
                          ? "Enter score (0-300), press Enter or Tab for next game and to save"
                          : undefined
                      }
                      disabled={!canEditScores}
                      placeholder={canEditScores ? "0-300" : ""}
                      aria-label={`${bowler.name} - Game ${
                        gameIndex + 1
                      } score`}
                      aria-describedby={
                        validationErrors[gameIndex]
                          ? `error-${teamId}-${bowler.id}-${gameIndex}`
                          : `score-help-${teamId}-${bowler.id}-${gameIndex}`
                      }
                      aria-invalid={!!validationErrors[gameIndex]}
                    />

                    {/* Hidden help text for screen readers */}
                    <div
                      id={`score-help-${teamId}-${bowler.id}-${gameIndex}`}
                      className="sr-only"
                    >
                      Enter score for {bowler.name} in Game {gameIndex + 1}.
                      Score must be between 0 and 300. Press Enter to save or
                      Tab to next field.
                    </div>
                    {validationErrors[gameIndex] && (
                      <p
                        id={`error-${teamId}-${bowler.id}-${gameIndex}`}
                        className="text-xs text-red-500 mt-1 text-center"
                        role="alert"
                        aria-live="polite"
                      >
                        {validationErrors[gameIndex]}
                      </p>
                    )}
                    {isScoreSaving(teamId, bowler.id, gameIndex) && (
                      <p
                        className="text-xs text-brand-muted mt-1"
                        aria-live="polite"
                        aria-label="Saving score"
                      >
                        Saving...
                      </p>
                    )}
                  </div>
                </div>
              )
            )}
          </div>
        </td>

        {/* Scratch Total - hidden on mobile */}
        <td className="hidden md:table-cell p-2.5 text-center font-score">
          {isCalculatingTotals ? (
            <span className="text-brand-mist">Calculating...</span>
          ) : (
            bowlerScratchTotal
          )}
          {/* Screen reader announcement for calculation status */}
          {isCalculatingTotals && (
            <div className="sr-only" aria-live="polite">
              Calculating team totals for {bowler.name}...
            </div>
          )}
        </td>

        {/* Handicap Total - hidden on mobile */}
        <td className="hidden md:table-cell p-2.5 text-center font-score">
          {isCalculatingTotals ? (
            <span className="text-brand-mist">Calculating...</span>
          ) : (
            bowlerHandicapTotal
          )}
        </td>
      </tr>
    );
  }
);

BowlerScoreRow.displayName = "BowlerScoreRow";

export default BowlerScoreRow;
